<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  operation: { type: Object, required: true },
  busy: { type: Boolean, default: false },
});
defineEmits(['resume', 'newTicket']);
const { t } = useI18n();
const saved = computed(() => props.operation.state === 'saved');
const pending = computed(() =>
  ['queued', 'waiting', 'processing', 'unknown'].includes(props.operation.state)
);
const title = computed(() =>
  t(`GG_SUPPORT.CREATION.STATES.${props.operation.state.toUpperCase()}`)
);
const issue = computed(() => {
  const code = props.operation.last_error_code;
  const known = [
    'DRAFT_REPLACED',
    'ACCESS_DENIED',
    'AUTH_INVALID',
    'IDEMPOTENCY_CONFLICT',
    'WRITE_DISABLED',
    'INTEGRATION_DISABLED',
    'DATABASE_NOT_APPROVED',
    'TYPE_NOT_SUPPORTED',
    'CUSTOMER_NOT_FOUND',
    'CUSTOMER_NAME_TOO_LONG',
    'TECHNICIAN_NOT_ELIGIBLE',
    'INVALID_REQUEST',
    'NOTE_FAILED',
    'LEGACY_OPERATION_REVIEW_REQUIRED',
    'TYPE_FIELDS_INVALID',
    'REFERENCE_NOT_ELIGIBLE',
    'ORDER_ITEM_NOT_ELIGIBLE',
    'EQUIPMENT_NOT_ELIGIBLE',
    'SCHEDULE_PARTICIPANT_NOT_ELIGIBLE',
  ];
  return code && known.includes(code)
    ? t(`GG_SUPPORT.CREATION.ERRORS.${code}`)
    : t('GG_SUPPORT.CREATION.RECOVERY_HINT');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      role="status"
      aria-live="polite"
      class="flex items-center gap-2 rounded-lg bg-n-blue-2 p-3 text-sm text-n-blue-11"
    >
      <Spinner v-if="pending" />
      <span class="font-semibold">{{ title }}</span>
    </div>
    <template v-if="saved">
      <article
        v-for="ticket in operation.result.tickets"
        :key="ticket.chamado_id"
        class="rounded-lg border border-n-weak p-3 flex flex-col gap-2"
      >
        <span class="text-xs text-n-slate-11">{{
          t(
            ticket.role === 'primary'
              ? 'GG_SUPPORT.CREATION.PRIMARY'
              : 'GG_SUPPORT.CREATION.ACTIVATION'
          )
        }}</span>
        <h3 class="m-0 text-lg font-semibold text-n-slate-12">
          {{ ticket.ticket_code }}
        </h3>
        <dl class="m-0 flex flex-col gap-2 text-sm text-n-slate-12">
          <div>
            <dt class="text-xs text-n-slate-11">
              {{ t('GG_SUPPORT.CREATION.TICKET_ID') }}
            </dt>
            <dd class="m-0">{{ ticket.chamado_id }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-11">
              {{ t('GG_SUPPORT.FIELDS.type') }}
            </dt>
            <dd class="m-0">{{ ticket.type }}</dd>
          </div>
          <div v-if="ticket.customer_id">
            <dt class="text-xs text-n-slate-11">
              {{ t('GG_SUPPORT.CUSTOMER') }}
            </dt>
            <dd class="m-0 break-words">
              {{ ticket.customer_id }} · {{ ticket.customer_name }}
            </dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-11">
              {{ t('GG_SUPPORT.RESPONSIBLE') }}
            </dt>
            <dd class="m-0 break-words">
              {{ operation.result.responsible.name }}
            </dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-11">
              {{ t('GG_SUPPORT.FIELDS.priority') }}
            </dt>
            <dd class="m-0">{{ ticket.priority }}</dd>
          </div>
          <div v-if="ticket.links.equipment_ids.length">
            <dt class="text-xs text-n-slate-11">
              {{ t('GG_SUPPORT.FIELDS.equipment_ids') }}
            </dt>
            <dd class="m-0 break-words">
              {{ ticket.links.equipment_ids.join(', ') }}
            </dd>
          </div>
          <div v-if="ticket.links.order">
            <dt class="text-xs text-n-slate-11">
              {{ t('GG_SUPPORT.FIELDS.order') }}
            </dt>
            <dd class="m-0">
              {{ ticket.links.order.kind }} · {{ ticket.links.order.code }} ·
              {{ ticket.links.order.id }}
            </dd>
          </div>
          <div v-if="ticket.schedule">
            <dt class="text-xs text-n-slate-11">
              {{ t('GG_SUPPORT.SCHEDULE') }}
            </dt>
            <dd class="m-0">
              {{
                t('GG_SUPPORT.CREATION.SCHEDULE_ID', {
                  id: ticket.schedule.event_id,
                })
              }}
            </dd>
          </div>
        </dl>
      </article>
      <p class="m-0 text-sm text-n-slate-11">
        {{
          t(`GG_SUPPORT.CREATION.NOTE.${operation.note_status.toUpperCase()}`)
        }}
      </p>
      <Button
        v-if="operation.note_status === 'failed'"
        :label="t('GG_SUPPORT.CREATION.RETRY_NOTE')"
        :disabled="busy"
        @click="$emit('resume')"
      />
    </template>
    <p v-else class="m-0 text-sm text-n-slate-11">{{ issue }}</p>
    <Button
      v-if="operation.state === 'blocked'"
      :label="t('GG_SUPPORT.CREATION.RESUME')"
      :disabled="busy"
      @click="$emit('resume')"
    />
    <Button
      v-if="
        (saved && operation.note_status === 'done') ||
        operation.state === 'failed'
      "
      :label="t('GG_SUPPORT.CREATION.NEW_TICKET')"
      color="slate"
      variant="faded"
      :disabled="busy"
      @click="$emit('newTicket')"
    />
  </div>
</template>
